import React, { useEffect, useState } from "react";
import { Box, Typography, Grid2, Paper, Select, InputLabel, FormControl, MenuItem, Stack, IconButton, Button, useTheme, useMediaQuery, Card, CardContent, CardHeader, Avatar } from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import Guage from "../Components/Guage/Guage";
import { DateTime } from "luxon";
import useGetSemesters from "../Hooks/useGetSemester";
import { useDispatch, useSelector } from "react-redux";
import useGetLocations from "../Hooks/useGetLocation";
import API from "../api/api";
import { ArrowCircleDown, ArrowDropDown, ArrowDropUp, ArrowUpward, AssignmentRounded, CircleSharp, FileDownload, FileOpenOutlined, FileOpenSharp, FilePresent, Filter, Filter1Outlined, Filter1Rounded, FilterListOutlined, SchoolOutlined, StopCircle, TuneOutlined } from "@mui/icons-material";
import { ClockIcon, DatePicker } from "@mui/x-date-pickers";
import AssessmentsBChart from "../Components/BarCharts/AssessmentsBChart";
import SessionsBChart from "../Components/BarCharts/SessionsBChart";
import ProgramsBChart from "../Components/BarCharts/ProgramsBChart";
import TutorsBChart from "../Components/BarCharts/TutorsBChart";
import SessionsTrend from "../Components/SparkLine/SessionsTrend";
import { setSnackError } from '../Reducers/snackErrors';
import CycleGrowthBar from "../Components/BarCharts/CycleGrowthBar";
import ProgramGrowthChart from "../Components/LineCharts/ProgramGrowthChart";
import AbsentPresentChart from "../Components/BarCharts/AbsentPresentChart";
import AssessmentProgression from "../Components/FunnelCharts/AssessmentProgression";
import SentimentChart from "../Components/BarCharts/SentimentChart";
import SimpleSentimentTable from "../Components/Table/SimpleSentimentTable";
import { parseSentimentData } from "./AdminHomeHelper";
import SessionsVsScoreScatterPlot from "../Components/ScatterPlot/SessionsVsScoreScatterPlot";
import StudentAssessmentPivotTable from "../Components/Table/StudentsAssessmentPivotTable";
import TutorSessionsByDateTable from "../Components/Table/TutorSessionByDateTable";
import Gate from "./Gate";
import { ROOT } from "../Static/static";
import TutorLowPerformance from "../Components/BarCharts/TutorLowPerformance";
import TutorsProgramWorkloadBar from "../Components/BarCharts/TutorsProgramWorkloadBar";


// Formik validation schema
// Locations side bar show all information from program, semester, subjects, primary contacts ... 
// Tutors show all sessions, assessments, surveys completed ... 

const validationSchema = Yup.object({
  semester_id: Yup.number().nullable(), // Allow null initially
  location_id: Yup.number().nullable(), // Allow null initially
  start_date: Yup.date().nullable(),
  end_date: Yup.date().nullable()
});

const AdminHome = () => {

    const dispatch = useDispatch();

    const role = useSelector((state) => state.role);
    const perms = useSelector((state) => state.role.permissions);
    const sysRole = useSelector((state) => state.role.role);

    const canGlobal = perms.includes("view:analytics:global");
    const canLocal  = perms.includes("view:analytics:local");
    const canAssessment  = perms.includes("view:analytics:assessment");
    const canTutor  = perms.includes("view:analytics:tutor");
    const canProgram  = perms.includes("view:analytics:program");
    const canSession  = perms.includes("view:analytics:session");
    
    const [guageData, setGuageData] = useState({
      sessionCount: 0,
        assessmentCount: 0,
        studentCount: 0
    });

    const [sessionsBChart, setSessionBChart] = useState([]);
    const [assessmentBChart, setAssessmentBChart] = useState([]);
    const [programsBChart, setProgramsBChart] = useState([]);
    const [tutorsBChart, setTutorsBChart] = useState([]);
    const [assessmentTrendData, setAssessmentTrend] = useState([]);
    const [sessionTrendData, setSessionTrend] = useState([]);
    const [lowPerformanceTutors, setLowPerformanceTutors] = useState([]);
    const [cycleGrowth, setCycleGrowth] = useState([]);
    const [cycleGrowthDelim, setCycleDelimGrowth] = useState([]);
    const [sentimentData, setSentimentData] = useState([]);
    const [absentPresent, setAbsentPresent] = useState({});
    const [assessmentCompletion, setAssessmentCompletion] = useState({});
    const [sessionVScore, setSessionVScore] = useState([]);
    const [studentVScore, setStudentVScore] = useState([]);
    const [tutors, setTutors] = useState([]);
    const [students, setStudents] = useState([]);
    const [tutorsSessions, setTutorsSessions] = useState([]);
    const [locationAverageSessions, setLocationSessions] = useState([]);
    


    const { semesters, loading: semestersLoading, error: semestersError } = useGetSemesters(role);
    const { locations, loading: locationsLoading, error: locationsError } = useGetLocations(role);

    const [dates, setDate] = useState({
        Today: DateTime.local().toSQLDate(),
        ThisWeek: `${DateTime.local().startOf('week').toSQLDate() }`+ "-" + `${DateTime.local().endOf('week').toSQLDate()}`,
        LastWeek: `${DateTime.local().startOf('week').minus({days:7}).toSQLDate() }`+"-"+`${DateTime.local().endOf('week').minus({days:7}).toSQLDate()}`,
        LastMonth: `${DateTime.local().startOf('month').toSQLDate() }`+"-"+`${DateTime.local().endOf('month').toSQLDate()}`,
    })

    const fallback = (req) => {
      return (
        <Paper sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
              You do not have access { req }
            </Typography>
          </Paper>
      )
    }

    // Initial form values
    const initialValues = {
        semester_id: null, // Initially null
        location_id: null, // Initially null
        start_date: null,
        end_date: null
    };

    // Handle form submission
    const handleSubmit = async (values) => {
        let start_date_parse, end_date_parse = null;
        if (values.start_date) {
          start_date_parse = DateTime.fromJSDate(new Date(values.start_date)).toSQLDate()
        }
        if (values.end_date) {
          end_date_parse = DateTime.fromJSDate(new Date(values.end_date)).toSQLDate()  
        }
        const promises = [
          loadSessionBChartData(values.location_id, values.semester_id, start_date_parse, end_date_parse),
          loadAssessmentBChart(values.location_id, values.semester_id, start_date_parse, end_date_parse),
          loadProgramsBChart(values.location_id, values.semester_id, start_date_parse, end_date_parse),
          loadTutorsBChart(values.location_id, values.semester_id, start_date_parse, end_date_parse),
          loadLowPerformanceTutorsData(values.location_id, values.semester_id, start_date_parse, end_date_parse),
          loadCycleGrowthData(values.location_id, values.semester_id, start_date_parse, end_date_parse),
          loadCycleGrowthDataPrePostMid(values.location_id, values.semester_id, start_date_parse, end_date_parse),
          loadAbsentPresentData(values.location_id, values.semester_id, start_date_parse, end_date_parse),
          loadAssessmentCompletion(values.location_id, values.semester_id, start_date_parse, end_date_parse),
          loadSentimentData(values.location_id, values.semester_id, start_date_parse, end_date_parse),
          loadSessionVScores(values.location_id, values.semester_id, start_date_parse, end_date_parse),
          loadStudentVAssessments(values.location_id, values.semester_id, start_date_parse, end_date_parse),
          loadTutors(values.location_id),
          loadStudentByLocation(values.location_id, values.semester_id),
          loadTutorRecentSessions(values.location_id, values.semester_id),
          loadLocationSessionAverage(values.location_id, values.semester_id),
        ];
        // Await all promises to complete in parallel
        await Promise.all(promises);
    };


    const loadTutors = async (location_id) => {
      if (!location_id) { return ;}
      try {
        const response = await API.get('/get_tutors', 
          {params: { email: role.email, role: role.role, id: role.id, organization_id: role.organization_id, location_id: location_id }})
        if (!response.data.data) {
          setTutors([]);
          return;
        }
        if (response.data.data) {
          setTutors(response.data.data);
          return;
        }
      }
      catch(error) {
        console.log(error)
        if (error.isAuthError || error.isForbidden) {
            return;
          } 
          if (error.isNetworkError) {
            dispatch(setSnackError({ 
              title: 'Connection Error', 
              body: error.message 
            }));
          } else {
            dispatch(setSnackError({
              title: 'Error found',
              body: error.message || 'Unable to get tutors.'
            }));
          }
        return;
      }
    }

    const loadGuageData = async (location_id, semester_id, start_date, end_date) => {
        try {
            const request = await API.get('/get_session_analytics', {params:{
              email: role.email, role: role.role, id: role.id, organization_id: role.organization_id, 
              location_id, semester_id, start_date, end_date} });
            setGuageData({
                sessionCount: request.data.session_count,
                assessmentCount: request.data.assessment_count,
                studentCount: request.data.student_count
            })
        }
        catch(error) {
          console.log(error)
          if (error.isAuthError || error.isForbidden) {
              return;
            } 
            if (error.isForbidden) {
              return;
            }
            if (error.isNetworkError) {
              dispatch(setSnackError({ 
                title: 'Connection Error', 
                body: error.message 
              }));
            } else {
              dispatch(setSnackError({
                title: 'Error found',
                body: error.message || 'Unable to get data.'
              }));
            }
          return;
        }
    }

    const loadSessionBChartData = async (location_id, semester_id, start_date, end_date) => {
        try {
            const request = await API.get('/get_session_bchart', {params: {email: role.email, role: role.role, id: role.id, organization_id: role.organization_id, 
              location_id, semester_id, start_date, end_date }});
              if (request.status === 200) {
                setSessionBChart(request.data.data);
              }
        }
        catch(error) {
          console.log(error)
          if (error.isAuthError || error.isForbidden) {
              return;
            } 
            if (error.isForbidden) {
              return;
            }
            if (error.isNetworkError) {
              dispatch(setSnackError({ 
                title: 'Connection Error', 
                body: error.message 
              }));
            } else {
              dispatch(setSnackError({
                title: 'Error found',
                body: error.message || 'Unable to get data.'
              }));
            }
          return;
        }
    }

    const loadAssessmentTrendData = async (location_id, semester_id, start_date, end_date) => {
      try {
          const request = await API.get('/get_assessment_trend', {params: {email: role.email, role: role.role, id: role.id, organization_id: role.organization_id, 
          location_id, semester_id, start_date, end_date }});
          if(request.status === 200) {setAssessmentTrend(request.data.data);}
      }
      catch(error) {
        if (error.isAuthError || error.isForbidden) {
            return;
          } 
          if (error.isNetworkError) {
            dispatch(setSnackError({ 
              title: 'Connection Error', 
              body: error.message 
            }));
          } else {
            dispatch(setSnackError({
              title: 'Error found',
              body: error.message || 'Unable to get data.'
            }));
          }
        return;
      }
    }
    
    const loadSessionTrendData = async (location_id, semester_id, start_date, end_date) => {
      try {
          const request = await API.get('/get_session_trend', {params: {email: role.email, role: role.role, id: role.id, organization_id: role.organization_id, 
            location_id, semester_id, start_date, end_date }});
          if (request.status === 200) {setSessionTrend(request.data.data);}
      }
      catch(error) {
        console.log(error)
          if (error.isAuthError || error.isForbidden) {
            return;
          } 
          if (error.isNetworkError) {
            dispatch(setSnackError({ 
              title: 'Connection Error', 
              body: error.message 
            }));
          } else {
            dispatch(setSnackError({
              title: 'Error found',
              body: error.message || 'Unable to get data.'
            }));
          }
        return;
      }
    }

    const loadAbsentPresentData = async (location_id, semester_id, start_date, end_date) => {
      try {
          const request = await API.get('/get_absent_present', {params: {email: role.email, role: role.role, id: role.id, organization_id: role.organization_id, 
            location_id, semester_id, start_date, end_date }});
          if (request.status === 200) {setAbsentPresent(request.data.data);}
      }
      catch(error) {
        console.log(error)
          if (error.isAuthError || error.isForbidden) {
            return;
          } 
          if (error.isNetworkError) {
            dispatch(setSnackError({ 
              title: 'Connection Error', 
              body: error.message 
            }));
          } else {
            dispatch(setSnackError({
              title: 'Error found',
              body: error.message || 'Unable to get data.'
            }));
          }
        return;
      }
    }

    const loadAssessmentCompletion = async (location_id, semester_id, start_date, end_date) => {
      try {
          const request = await API.get('/get_assessment_completion', {params: {email: role.email, role: role.role, id: role.id, organization_id: role.organization_id, 
            location_id, semester_id, start_date, end_date }});
          if (request.status === 200) {setAssessmentCompletion(request.data.data);}
      }
      catch(error) {
        console.log(error)
          if (error.isAuthError || error.isForbidden) {
            return;
          } 
          if (error.isNetworkError) {
            dispatch(setSnackError({ 
              title: 'Connection Error', 
              body: error.message 
            }));
          } else {
            dispatch(setSnackError({
              title: 'Error found',
              body: error.message || 'Unable to get data.'
            }));
          }
        return;
      }
    }

    const loadCycleGrowthData = async (location_id, semester_id, start_date, end_date) => {
      try {
          const request = await API.get('/get_cycle_growth', {params: {email: role.email, role: role.role, id: role.id, organization_id: role.organization_id, 
            location_id, semester_id, start_date, end_date }});
          if (request.status === 200) {setCycleGrowth(request.data.data);}
      }
      catch(error) {
        console.log(error)
          if (error.isAuthError || error.isForbidden) {
            return;
          } 
          if (error.isNetworkError) {
            dispatch(setSnackError({ 
              title: 'Connection Error', 
              body: error.message 
            }));
          } else {
            dispatch(setSnackError({
              title: 'Error found',
              body: error.message || 'Unable to get data.'
            }));
          }
        return;
      }
    }

    const loadCycleGrowthDataPrePostMid = async (location_id, semester_id, start_date, end_date) => {
      try {
          const request = await API.get('/get_cycle_growth_delimiters', {params: {email: role.email, role: role.role, id: role.id, organization_id: role.organization_id, 
            location_id, semester_id, start_date, end_date }});
          if (request.status === 200) {setCycleDelimGrowth(request.data.data);}
      }
      catch(error) {
        console.log(error)
          if (error.isAuthError || error.isForbidden) {
            return;
          } 
          if (error.isNetworkError) {
            dispatch(setSnackError({ 
              title: 'Connection Error', 
              body: error.message 
            }));
          } else {
            dispatch(setSnackError({
              title: 'Error found',
              body: error.message || 'Unable to get data.'
            }));
          }
        return;
      }
    }

    const loadLowPerformanceTutorsData = async (location_id, semester_id, start_date, end_date) => {
      try {
          const request = await API.get('/get_tutor_low_performance', {params: {email: role.email, role: role.role, id: role.id, organization_id: role.organization_id, 
            location_id, semester_id, start_date, end_date }});
          if (request.status === 200) {setLowPerformanceTutors(request.data);}
      }
      catch(error) {
        console.log(error)
          if (error.isAuthError || error.isForbidden) {
            return;
          } 
          if (error.isNetworkError) {
            dispatch(setSnackError({ 
              title: 'Connection Error', 
              body: error.message 
            }));
          } else {
            dispatch(setSnackError({
              title: 'Error found',
              body: error.message || 'Unable to get data.'
            }));
          }
        return;
      }
    }

    const loadAssessmentBChart = async (location_id, semester_id, start_date, end_date) => {
        try {
            const request = await API.get('/get_assessment_bchart', {params: {email: role.email, role: role.role, id: role.id, organization_id: role.organization_id, 
              location_id, semester_id, start_date, end_date }});
            if (request.status === 200) {setAssessmentBChart(request.data.data);}
        }
        catch(error) {
          console.log(error)
          if (error.isAuthError || error.isForbidden) {
              return;
            } 
            if (error.isForbidden) {
              return;
            } 
            if (error.isNetworkError) {
              dispatch(setSnackError({ 
                title: 'Connection Error', 
                body: error.message 
              }));
            } else {
              dispatch(setSnackError({
                title: 'Error found',
                body: error.message || 'Unable to get data.'
              }));
            }
          return;
        }
    }

    const loadProgramsBChart = async (location_id, semester_id, start_date, end_date) => {
      try {
          const request = await API.get('/get_programs_bchart', {params: {email: role.email, role: role.role, id: role.id, organization_id: role.organization_id, 
            location_id, semester_id, start_date, end_date }});
          if (request.status === 200) {setProgramsBChart(request.data.data);}
      }
      catch(error) {
        if (error.isAuthError || error.isForbidden) {
            return;
          } 
          if (error.isNetworkError) {
            dispatch(setSnackError({ 
              title: 'Connection Error', 
              body: error.message 
            }));
          } else {
            dispatch(setSnackError({
              title: 'Error found',
              body: error.message || 'Unable to get program bchart.'
            }));
          }
        return;
      }
    }

    const loadTutorsBChart = async (location_id, semester_id, start_date, end_date) => {
      try {
          const request = await API.get('/get_tutors_bchart', {params: {email: role.email, role: role.role, id: role.id, organization_id: role.organization_id, 
            location_id, semester_id, start_date, end_date }});
          if (request.status === 200) {setTutorsBChart(request.data.data);}
      }
      catch(error) {
        console.log(error)
        if (error.isAuthError || error.isForbidden) {
            return;
          } 
          if (error.isForbidden) {
            return;
          }
          if (error.isNetworkError) {
            dispatch(setSnackError({ 
              title: 'Connection Error', 
              body: error.message 
            }));
          } else {
            dispatch(setSnackError({
              title: 'Error found',
              body: error.message || 'Unable to get data.'
            }));
          }
        return;
      }
    }

    const loadSentimentData = async (location_id, semester_id, start_date, end_date) => {
      try {
          const request = await API.get('/get_sentiment', 
              {params: { organization_id: role.organization_id, 
            location_id }});
          if (request.status === 200) {
              const arr = request.data.data;
              if (!arr) { return}
              if (arr && arr.length > 1) {
                  const d = parseSentimentData(arr)
                  setSentimentData(d)
                  console.log(d)
              }
          }
      }
      catch(error) {
        console.log(error)
        if (error.isAuthError || error.isForbidden) {
            return;
          } 
          if (error.isForbidden) {
            return;
          }
          if (error.isNetworkError) {
            dispatch(setSnackError({ 
              title: 'Connection Error', 
              body: error.message 
            }));
          } else {
            dispatch(setSnackError({
              title: 'Error found',
              body: error.message || 'Unable to get data.'
            }));
          }
        return;
      }
    }

    const loadSessionVScores = async (location_id, semester_id, start_date, end_date) => {
      try {
          const request = await API.get('/get_session_vscore', 
              {params: { organization_id: role.organization_id, 
            location_id, semester_id, start_date, end_date}});
          if (request.status === 200) {
              const arr = request.data.data;
              if (!arr) { return}
              if (arr && arr.length > 1) {
                setSessionVScore(arr);
              }
          }
      }
      catch(error) {
        console.log(error)
        if (error.isAuthError || error.isForbidden) {
            return;
          } 
          if (error.isForbidden) {
            return;
          }
          if (error.isNetworkError) {
            dispatch(setSnackError({ 
              title: 'Connection Error', 
              body: error.message 
            }));
          } else {
            dispatch(setSnackError({
              title: 'Error found',
              body: error.message || 'Unable to get data.'
            }));
          }
        return;
      }
    }

    const loadStudentVAssessments = async (location_id, semester_id, start_date, end_date) => {
      try {
          const request = await API.get('/get_student_vassessment', 
              {params: { organization_id: role.organization_id, 
            location_id, semester_id, start_date, end_date}});
          if (request.status === 200) {
              const arr = request.data.data;
              if (!arr) { return}
              if (arr && arr.length > 1) {
                setStudentVScore(arr);
              }
          }
      }
      catch(error) {
        console.log(error)
        if (error.isAuthError || error.isForbidden) {
            return;
          } 
          if (error.isForbidden) {
            return;
          }
          if (error.isNetworkError) {
            dispatch(setSnackError({ 
              title: 'Connection Error', 
              body: error.message 
            }));
          } else {
            dispatch(setSnackError({
              title: 'Error found',
              body: error.message || 'Unable to get data.'
            }));
          }
        return;
      }
    }

    const loadStudentByLocation = async (location_id, semester_id) => {
      if (!location_id && !semester_id) { return ;}
      try {
          const request = await API.get('/get_students',
            { params: { locationId: location_id}});
          if (request.data.data === null) {
              setStudents([]);
          }
          if (request.data.data) {
              setStudents(request.data.data);
          }
      }
      catch (error) {
        if (error.isAuthError || error.isForbidden) {
          return;
        }
        if (error.isNetworkError) {
          dispatch(setSnackError({ 
            title: 'Connection Error', 
            body: error.message 
          }));
        } else {
          dispatch(setSnackError({
            title: 'Error found',
            body: error.message || 'Unable to get students'
          }));
        }
      }
    }

    const loadTutorRecentSessions = async (location_id, semester_id) => {
      if (!location_id && !semester_id) { return ;}
      try {
          const request = await API.get('/get_recent_location_sessions', 
              {params: {location_id, semester_id }});
          if (request.status === 200) {
              const arr = request.data.data;
              if (!arr) { return}
              if (arr && arr.length > 1) {
                  setTutorsSessions(arr);
              }
          }
      }
      catch(error) {
        console.log(error)
        if (error.isAuthError || error.isForbidden) {
            return;
          } 
          if (error.isForbidden) {
            return;
          }
          if (error.isNetworkError) {
            dispatch(setSnackError({ 
              title: 'Connection Error', 
              body: error.message 
            }));
          } else {
            dispatch(setSnackError({
              title: 'Error found',
              body: error.message || 'Unable to get data.'
            }));
          }
        return;
      }
  }

  const loadLocationSessionAverage = async (location_id, semester_id) => {
    if (!location_id && !semester_id) { return ;}
    try {
        const request = await API.get('/get_location_session_average', 
            {params: { organization_id: role.organization_id , location_id, semester_id }});
        if (request.status === 200) {
            const arr = request.data.data;
            if (!arr) { return}
            if (arr && arr.length > 1) {
              setLocationSessions(arr);
            }
        }
    }
    catch(error) {
      console.log(error)
      if (error.isAuthError || error.isForbidden) {
          return;
        } 
        if (error.isForbidden) {
          return;
        }
        if (error.isNetworkError) {
          dispatch(setSnackError({ 
            title: 'Connection Error', 
            body: error.message 
          }));
        } else {
          dispatch(setSnackError({
            title: 'Error found',
            body: error.message || 'Unable to get data.'
          }));
        }
      return;
    }
  }
    useEffect(() => {
      loadAssessmentTrendData(null, null, null, null);
      loadSessionTrendData(null, null, null, null);
      loadGuageData(null, null, null, null);
    }, [])

    const handleDateStartChange = async (value, setFieldValue) => {
        try {
          setFieldValue('start_date', value);
        }
        catch(error) {
          console.log(error);
          throw new Error(error);
        }
    }
    const handleDateEndChange = async (value, setFieldValue) => {
        try {
          setFieldValue('end_date', value);
        }
        catch(error) {
          console.log(error);
          throw new Error(error);
        }
    }

  return (
    <Box sx={{ flexGrow: 1, p: 1 }}>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, handleChange, handleBlur, setFieldValue }) => (
          <Form>
            <Grid2 container spacing={2} alignItems="center" sx={{ p: 2 }}>
              <Grid2 size={{xs:12, md:4}}>
                <Typography variant="h5" fontWeight="bold" sx={{ mb: { xs: 1, md: 0 } }}>
                  Admin Dashboard
                </Typography>
              </Grid2>

              <Grid2 size={{xs:12, md:8}}>
                <Stack 
                  direction={{ xs: 'row', sm: 'row' }} 
                  spacing={1} 
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  flexWrap="wrap"
                  useFlexGap
                >
                  <IconButton disabled sx={{ alignSelf: 'center' }}>
                    <FilterListOutlined />
                  </IconButton>

                  <FormControl size="small" sx={{ minWidth: 80, flexGrow: 1 }}>
                    <InputLabel id="semester-select-label">Semester</InputLabel>
                    <Field
                      as={Select}
                      labelId="semester-select-label"
                      name="semester_id"
                      label="Semester"
                      value={values.semester_id || ""}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    >
                      <MenuItem value="">
                        <Typography variant="body2">Select Semester</Typography>
                      </MenuItem>
                      {semesters.map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.year} - {item.title}
                        </MenuItem>
                      ))}
                    </Field>
                  </FormControl>

                  <FormControl size="small" sx={{ minWidth: 80, flexGrow: 1 }}>
                    <InputLabel id="location-select-label">Location</InputLabel>
                    <Field
                      as={Select}
                      labelId="location-select-label"
                      name="location_id"
                      label="Location"
                      value={values.location_id || ""}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    >
                      <MenuItem value="">
                        <Typography variant="body2">Select Location</Typography>
                      </MenuItem>
                      {locations.map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </Field>
                  </FormControl>

                  <FormControl size="small" sx={{ minWidth: 50,  flexGrow: 0 }}>
                    <DatePicker
                      label="Start Date"
                      name="start_date"
                      value={values.start_date}
                      onChange={(value) => handleDateStartChange(value, setFieldValue)}
                      slotProps={{
                        textField: {
                          size: 'small',
                          sx: {
                            '& .MuiInputBase-root': {
                              height: 40, // Adjust this value to make it smaller
                            },
                          },
                        },
                      }}
                    />
                  </FormControl>

                  <FormControl size="small" sx={{ minWidth: 80, flexGrow: 0 }}>
                    <DatePicker
                      label="End Date"
                      name="end_date"
                      value={values.end_date}
                      onChange={(value) => handleDateEndChange(value, setFieldValue)}
                      slotProps={{
                        textField: {
                          size: 'small',
                          sx: {
                            '& .MuiInputBase-root': {
                              height: 40, // Same as above for consistency
                            },
                          },
                        },
                      }}
                    />
                  </FormControl>

                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    type="submit"
                    startIcon={<ArrowCircleDown />}
                    sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
                  >
                    Search
                  </Button>
                </Stack>
              </Grid2>
            </Grid2>
          </Form>
        )}
      </Formik>
      <Grid2 container spacing={2}>

        <Grid2 size={{xs: 12, sm: 6, md: 6, lg: 6}} direction="column" spacing={2}>
          <Gate allow={sysRole === ROOT || canGlobal} fallback={fallback("view:analytics:global")}>
            <Grid2 container spacing={2}>
            <Grid2 item size={{xs: 12, sm: 4, xl: 4}}>
              <Guage
                  max={guageData.sessionCount}
                  value={guageData.sessionCount}
                  icon={<ClockIcon />}
                  label="Sessions"
                  body={`Sessions Today`}
                />
            </Grid2>
            <Grid2 item size={{xs: 12, sm: 4, xl: 4}}>
                <Guage
                  value={guageData.assessmentCount}
                  max={guageData.assessmentCount}
                  icon={<AssignmentRounded />}
                  label="Assessments"
                  body={`Assessments Today`}
                />
            </Grid2>
            <Grid2 item size={{xs: 12, sm: 4, xl: 4}}>
                <Guage
                  value={guageData.studentCount}
                  max={guageData.studentCount}
                  icon={<SchoolOutlined />}
                  label="Students"
                  body={`Students helped Today`}
                />
            </Grid2>
            <Grid2 size={{xs: 12, sm: 12, md: 12, lg: 12}} direction="column" spacing={2}>
            <Grid2 container spacing={2}>
              <Grid2 item size={{xs: 12}}>
                <Paper elevation={1}>
                      <Box>
                        <Typography variant="subtitle2" textAlign={'center'} fontWeight={'bold'}>
                            Global data
                        </Typography>
                      </Box>
                </Paper>
              </Grid2>
            </Grid2>
            </Grid2>
            </Grid2>
          </Gate>
          <Gate allow={sysRole === ROOT || canLocal} fallback={fallback("view:analytics:local")}>
          <Grid2 container spacing={2} sx={{pt:2}}>
            <Grid2 item size={{xs: 12, sm: 4, xl: 4}}>
                  <Guage
                    value={tutors.length}
                    icon={<SchoolOutlined />}
                    label="Tutors"
                    body={`Tutors available`}
                  />
              </Grid2>
              <Grid2 item size={{xs: 12, sm: 4, xl: 4}}>
                  <Guage
                    value={students.length}
                    icon={<SchoolOutlined />}
                    label="Students"
                    body={`Students enrolled`}
                  />
              </Grid2>
              <Grid2 item size={{xs: 12, sm: 4, xl: 4}}>
                  <Guage
                    value={guageData.studentCount}
                    max={guageData.studentCount}
                    icon={<SchoolOutlined />}
                    label="Students"
                    body={`Assessment growth`}
                  />
              </Grid2>
          </Grid2>
          </Gate>
        </Grid2>

        <Gate allow={sysRole === ROOT || canLocal} fallback={fallback("view:analytics:local")}>
        <Grid2 size={{xs:12, sm: 6, md: 6, lg: 6}} direction={"column"} spacing={2}> 
         <Grid2 container spacing={2}>
         <Grid2 item size={{xs: 12, sm: 6, md: 6, lg: 6}}>
              <Paper elevation={2} sx={{ p: 1, borderRadius: 0 }}>
                <ProgramsBChart data={programsBChart} />
              </Paper>
          </Grid2>
            <Grid2 item size={{xs: 12, sm: 6, md: 6, lg: 6}}>
              <Paper elevation={2} sx={{ p: 1, borderRadius: 0 }}>
                <AbsentPresentChart counts={absentPresent} />
              </Paper>
            </Grid2>
         </Grid2>
        </Grid2>  
        </Gate>
        
        <Grid2 size={{xs: 12, sm: 12, md: 12, lg: 12}} direction="column" spacing={2}>
          <Grid2 container spacing={2}>
            <Grid2 item size={{xs: 12}}>
              <Paper elevation={1}>
                    <Box>
                      <Typography variant="subtitle2" textAlign={'center'} fontWeight={'bold'}>
                          Local data
                      </Typography>
                    </Box>
              </Paper>
            </Grid2>
          </Grid2>
        </Grid2>
      </Grid2>

      <Grid2 container spacing={2} sx={{pt:2}}>

      <Gate allow={sysRole === ROOT || canAssessment} fallback={fallback("view:analytics:assessment")}>
        <Grid2 size={{xs: 12, sm: 6, md: 6, lg: 6}} direction="column" spacing={2}>
          <Grid2 container spacing={2}>
            <Grid2 item size={{xs: 12}}>
              <Paper elevation={2} sx={{ p: 1, borderRadius: 0 }}>
                <AssessmentsBChart data={assessmentBChart} />
              </Paper>
            </Grid2>
          </Grid2>
        </Grid2>

        <Grid2 size={{xs:12, sm: 6, md: 6, lg: 6}} direction={"column"} spacing={2}> 
            <Grid2 container spacing={2}>
              <Grid2 item size={{xs: 12}}>
              <Paper elevation={2} sx={{ p: 1, borderRadius: 0 }}>
                <AssessmentProgression data={assessmentCompletion} />
              </Paper>
            </Grid2>
            </Grid2>
        </Grid2>
        
        <Grid2 size={{xs: 12, sm: 12, md: 12, lg: 12}} direction="column" spacing={2}>
          <Grid2 container spacing={2}>

          <Grid2 item size={{xs: 12, sm: 12, md: 12, lg: 12}}>
            <Paper elevation={2} sx={{ p: 1, borderRadius: 0 }}>
              <StudentAssessmentPivotTable data={studentVScore} />
            </Paper>
          </Grid2>

            <Grid2 item size={{xs: 12}}>
              <Paper elevation={1}>
                    <Box>
                      <Typography variant="subtitle2" textAlign={'center'} fontWeight={'bold'}>
                          Assessment data
                      </Typography>
                    </Box>
              </Paper>
            </Grid2>
          </Grid2>

        </Grid2>
      </Gate>
        
      </Grid2>

      <Grid2 container spacing={2} sx={{pt: 2}}>

      <Gate allow={sysRole === ROOT || canProgram} fallback={fallback("view:analytics:program")}>
      <Grid2 size={{xs: 12, sm: 6, md: 6, lg: 6}} direction="column" spacing={2}>
          <Grid2 container spacing={2}>
          <Grid2 item size={{xs: 12}}>
              <Paper elevation={2} sx={{ p: 1, borderRadius: 0 }}>
                  <ProgramGrowthChart data={cycleGrowthDelim} />
                </Paper>
            </Grid2>  
          </Grid2>
      </Grid2>

      <Grid2 size={{xs: 12, sm: 6, md: 6, lg: 6}} direction="column" spacing={2}>
          <Grid2 container spacing={2}>
            <Grid2 item size={{xs: 12}}>
            <Paper elevation={2} sx={{ p: 1, borderRadius: 0 }}>
              <CycleGrowthBar data={cycleGrowth} />
            </Paper>
          </Grid2>
          </Grid2>
      </Grid2>
      

        <Grid2 item size={{xs: 12}}>
              <Paper elevation={1}>
                    <Box>
                      <Typography variant="subtitle2" textAlign={'center'} fontWeight={'bold'}>
                          Program data
                      </Typography>
                    </Box>
              </Paper>
        </Grid2>
        </Gate>
      </Grid2>
      

      <Grid2 container spacing={2} sx={{pt: 2}}>
        <Gate allow={sysRole === ROOT || canSession} fallback={fallback("view:analytics:session")}>
      <Grid2 size={{xs: 12, sm: 6, md: 6, lg: 6}} direction="column" spacing={2}>
          <Grid2 container spacing={2}>
          <Grid2 item size={{xs: 12}}>
            <Paper elevation={2} sx={{ p: 1 , borderRadius: 0}}>
                <SessionsBChart data={sessionsBChart} />
              </Paper>
            </Grid2>  
            <Grid2 item size={{xs: 12}}>
            <Paper elevation={2} sx={{ p: 1 , borderRadius: 0}}>
                <TutorsProgramWorkloadBar data={locationAverageSessions} />
              </Paper>
            </Grid2>  
          </Grid2>
      </Grid2>

      <Grid2 size={{xs: 12, sm: 6, md: 6, lg: 6}} direction="column" spacing={2}>
          <Grid2 container spacing={2}>
            <Grid2 item size={{xs: 12}}>
              <Paper elevation={2} sx={{ p: 1, borderRadius: 0 }}>
                <SessionsVsScoreScatterPlot data={sessionVScore} />
              </Paper>
            </Grid2>
            <Grid2 item size={{xs: 12}}>
              <Paper elevation={2} sx={{ p: 1, borderRadius: 0 }}>
                  <SimpleSentimentTable data={sentimentData} />
                </Paper>
            </Grid2>
          </Grid2>
      </Grid2>


      

        <Grid2 item size={{xs: 12}}>
              <Paper elevation={1}>
                    <Box>
                      <Typography variant="subtitle2" textAlign={'center'} fontWeight={'bold'}>
                          Session data
                      </Typography>
                    </Box>
              </Paper>
        </Grid2>
        </Gate>
      </Grid2>

      <Grid2 container spacing={2} sx={{pt: 2}}>
        {/* Left Column: Guage Items and Smaller Chart */}
        <Gate allow={sysRole === ROOT || canTutor} fallback={fallback("view:analytics:tutor")}>
        <Grid2 container size={{xs: 12, sm: 6, md: 6, lg: 6}} direction="column" spacing={2}>
            <Grid2 item size={{xs: 12, sm: 12, md: 12, lg: 12}}>
              <Paper elevation={2} sx={{ p: 1, borderRadius: 0}}>
                <TutorsBChart data={tutorsBChart} />
              </Paper>
            </Grid2>
            
        </Grid2>

        <Grid2 container size={{xs: 12, sm: 6, md: 6, lg: 6}} direction="column" spacing={2}>
            <Grid2 item size={{xs: 12, sm: 12, md: 12, lg: 12}}>
                <Paper elevation={1} sx={{p: 1, borderRadius: 0}}>
                  <TutorLowPerformance data={lowPerformanceTutors} />
                </Paper>

              </Grid2>            
        </Grid2>

        <Grid2 container size={{xs: 12, sm:12, md: 12, lg: 12}} direction={"column"} spacing={2}>

            <Grid2 item size={{xs: 12}}>
              <Paper elevation={1} sx={{ p: 1, borderRadius: 0 }}>
                <TutorSessionsByDateTable data={tutorsSessions} />
              </Paper>
            </Grid2>
         


          <Grid2 item size={{xs: 12}}>
              <Paper elevation={1}>
                    <Box>
                      <Typography variant="subtitle2" textAlign={'center'} fontWeight={'bold'}>
                          Tutor data
                      </Typography>
                    </Box>
              </Paper>
          </Grid2>
        </Grid2>

        </Gate>

      </Grid2>

          
    </Box>
  );
};

export default AdminHome;